import NiceModal from "@ebay/nice-modal-react";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import { FormHelperText, IconButton, Typography } from "@mui/material";
import { Box } from "@mui/system";
import { useCustomModel } from "components/context/CustomProvider";
import { LabelValueForText } from "components/ui/atoms/LabelValue";
import { useEffect, useMemo, useState } from "react";
import { useController } from "react-hook-form";
import ModelInfoButton from "../../components/ModelInfoButton";
import ObjectUpdateModal from "../../generics/ObjectUpdateModal";
import CustomSimpleTable from "../../table/CustomSimpleTable";
import modelConverters from "../../table/modelConverters";

const CustomStandardListField = ({ field, control }) => {
  const { field: fieldProps, fieldState } = useController({
    control,
    name: field.name,
  });

  const model = useCustomModel(field.child);
  const columns = modelConverters.getColumns(model);
  const initialState = modelConverters.getInitialState(model);
  const updateModal = ObjectUpdateModal(field.child);
  const [newRow, setNewRow] = useState(null);

  const value = fieldProps.value || [];

  const errorsDico = useMemo(() => {
    if (!fieldState.error?.message) return {};
    return Object.fromEntries(
      value
        .map((v, index) => {
          return [
            v.id,
            fieldState.error?.message
              ? fieldState.error.message[index]
              : undefined,
          ];
        })
        .filter(([_, error]) => error !== undefined),
    );
  }, [fieldState.error, value]);

  const handleRowClick = ({ row, ...props }) => {
    const submitter = ({ id, data }, { onSuccess, onError }) => {
      fieldProps.onChange(value.map((v) => (v.id === data.id ? data : v)));
      return onSuccess(data);
    };

    const deletter = (id, { onSuccess, onError }) => {
      fieldProps.onChange(value.filter((v) => v.id !== id));
      return onSuccess(id);
    };

    NiceModal.show(updateModal, {
      obj: row,
      submitter,
      deletter,
      errors: errorsDico[row.id],
    });
    document.activeElement.blur();
  };

  const createRandomId = () => {
    return "NEW_" + Math.random().toString(36).substring(2, 15);
  };

  const handleAdd = () => {
    const newRow = { id: createRandomId(), _title: "New Item" };
    fieldProps.onChange([...value, newRow]);
    setNewRow(newRow);
  };

  useEffect(() => {
    if (newRow) {
      setNewRow(null);
      handleRowClick({ row: newRow });
    }
  }, [newRow, handleRowClick]);

  const getRowClassName = useMemo(() => {
    return ({ id, indexRelativeToCurrentPage, ...params }) => {
      if (
        errorsDico[id] &&
        typeof errorsDico[id] === "object" &&
        Object.keys(errorsDico[id] || {}).length > 0
      ) {
        return "custom-error";
      }
      if (typeof id === "string" && id.startsWith("NEW_")) {
        return "custom-new-row";
      }
      return "";
    };
  }, [fieldState.error]);

  return (
    <>
      <Box>
        <LabelValueForText
          label={field.label}
          value={
            <IconButton
              onClick={handleAdd}
              variant="outlined"
              size="small"
              sx={{ mt: 1 }}
            >
              <AddCircleOutlineIcon />
            </IconButton>
          }
        />
        <CustomSimpleTable
          rows={value}
          columns={columns}
          initialState={initialState}
          onRowClick={handleRowClick}
          getRowClassName={getRowClassName}
        />
        <ModelInfoButton
          modelName={field.child}
          variant={"link"}
          label="model"
          sx={{ display: "block", textAlign: "right" }}
        />
        <FormHelperText error={!!fieldState.error}>
          {fieldState.error?.message.filter((e) => typeof e === "string") ||
            field.help_text ||
            ""}
        </FormHelperText>
      </Box>
    </>
  );
};

const CustomListField = ({ field, control }) => {
  const model = useCustomModel(field.child);

  if (model.formComponent)
    return <model.formComponent field={field} control={control} multiple />;

  return <CustomStandardListField field={field} control={control} />;
};

export default CustomListField;
